import React, { useState, useEffect, useRef } from "react";
import { API_URL } from "../../api-config";

// Hook animado para parpadeo al cambiar de valor
const useFlashColors = (value) => {
  const prevRef = useRef(value);
  const [flashClass, setFlashClass] = useState("transition-colors duration-[1500ms]");

  useEffect(() => {
    // Evitamos rebotar al inicializar
    if (value !== prevRef.current && prevRef.current !== undefined && prevRef.current !== 0) {
      // Color intenso al cambiar
      const flashColor = value > prevRef.current 
        ? "bg-[#00e676]/30 transition-none" 
        : "bg-[#ff3b30]/30 transition-none";
      
      setFlashClass(flashColor);
      prevRef.current = value;
      
      // Decaimiento (fade-out) súper suave después del flash
      const timer = setTimeout(() => {
        setFlashClass("transition-colors duration-[1500ms] bg-transparent");
      }, 50);
      return () => clearTimeout(timer);
    } else {
      prevRef.current = value;
    }
  }, [value]);

  return flashClass;
};

const OrderBookView = () => {
  const [bids, setBids] = useState([]);
  const [asks, setAsks] = useState([]);
  const [symbol] = useState("USDCLP");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const baseWsUrl = API_URL.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
    const wsUrl = `${baseWsUrl}/trading-api/historical/ws/orderbook/${symbol}`;
    
    setLoading(true);
    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data) {
          setBids(data.bids || []);
          setAsks(data.asks || []);
        }
        setLoading(false);
      } catch (e) {
        console.error("Parse WS error", e);
      }
    };

    ws.onerror = (err) => {
      console.error("WS Orderbook Error", err);
      setLoading(false);
    };

    return () => {
      if (ws.readyState === 1 || ws.readyState === 0) {
        ws.close();
      }
    };
  }, [symbol]);

  // Calculate generic max size for width visuals
  const maxBidSize = bids.length > 0 ? Math.max(...bids.map(b => b.size)) : 1;
  const maxAskSize = asks.length > 0 ? Math.max(...asks.map(a => a.size)) : 1;
  const maxSize = Math.max(maxBidSize, maxAskSize, 1);

  // Indicators calculations
  const bidDepth3 = bids.slice(0, 3).reduce((acc, l) => acc + l.size, 0);
  const askDepth3 = asks.slice(0, 3).reduce((acc, l) => acc + l.size, 0);

  const bidDepth5 = bids.slice(0, 5).reduce((acc, l) => acc + l.size, 0);
  const askDepth5 = asks.slice(0, 5).reduce((acc, l) => acc + l.size, 0);

  const bidDepth10 = bids.slice(0, 10).reduce((acc, l) => acc + l.size, 0);
  const askDepth10 = asks.slice(0, 10).reduce((acc, l) => acc + l.size, 0);

  const bestBid = bids.length > 0 ? bids[0].price : 0;
  const bestAsk = asks.length > 0 ? asks[0].price : 0;
  const spread = bestAsk > 0 && bestBid > 0 ? bestAsk - bestBid : 0;

  const calcVwap = (levels, target) => {
    let filled = 0; let cost = 0;
    for (let l of levels) {
      const take = Math.min(l.size, target - filled);
      cost += take * l.price;
      filled += take;
      if (filled >= target) break;
    }
    return filled > 0 ? cost / filled : 0;
  };
  
  const vwapBid5M = calcVwap(bids, 5000000);
  const vwapAsk5M = calcVwap(asks, 5000000);

  const vwapBid10M = calcVwap(bids, 10000000);
  const vwapAsk10M = calcVwap(asks, 10000000);

  const vwapBid20M = calcVwap(bids, 20000000);
  const vwapAsk20M = calcVwap(asks, 20000000);

  const vwapBid50M = calcVwap(bids, 50000000);
  const vwapAsk50M = calcVwap(asks, 50000000);

  // Hooking the flashing classes
  const flashBid = useFlashColors(bestBid);
  const flashAsk = useFlashColors(bestAsk);
  const flashSpread = useFlashColors(spread);
  
  const flashBidDepth3 = useFlashColors(bidDepth3);
  const flashAskDepth3 = useFlashColors(askDepth3);
  const flashBidDepth5 = useFlashColors(bidDepth5);
  const flashAskDepth5 = useFlashColors(askDepth5);
  const flashBidDepth10 = useFlashColors(bidDepth10);
  const flashAskDepth10 = useFlashColors(askDepth10);
  
  const flashVwapBid5M = useFlashColors(vwapBid5M);
  const flashVwapAsk5M = useFlashColors(vwapAsk5M);
  const flashVwapBid10M = useFlashColors(vwapBid10M);
  const flashVwapAsk10M = useFlashColors(vwapAsk10M);
  const flashVwapBid20M = useFlashColors(vwapBid20M);
  const flashVwapAsk20M = useFlashColors(vwapAsk20M);
  const flashVwapBid50M = useFlashColors(vwapBid50M);
  const flashVwapAsk50M = useFlashColors(vwapAsk50M);

  return (
    <div className="h-screen bg-[#101523] text-white p-2 md:p-4 font-sans flex flex-col overflow-hidden w-full mx-auto max-w-[1500px]">
        {/* Header - Compact */}
        <div className="flex justify-between items-center bg-[#1c2237] px-4 py-2 rounded-lg border border-[#262c40] shadow-md mb-2 md:mb-4 shrink-0">
          <div className="flex flex-col min-w-0">
              <h1 className="text-sm md:text-lg xl:text-xl font-bold tracking-wider truncate">Live Order Book - FIX Depth ({symbol})</h1>
          </div>
          <div className="flex gap-2 md:gap-4 items-center shrink-0">
            {loading && <div className="animate-spin h-3 w-3 md:h-5 md:w-5 border-2 border-t-[#28b1c5] rounded-full"></div>}
            <span className="text-[#28b1c5] font-semibold text-[10px] md:text-xs animate-pulse ml-2">● LIVE</span>
          </div>
        </div>

        <div className="flex flex-1 gap-2 xl:gap-4 overflow-hidden pb-2 xl:pb-4 w-full">
            {/* LADO IZQUIERDO: Analíticas de Compras (Bids) */}
            <div className={`w-[25%] lg:w-1/4 flex flex-col p-2 xl:p-4 rounded-lg border border-[#262c40] bg-[#1c2237] overflow-y-auto shadow-md border-t-4 border-t-[#00e676] min-w-[120px]`}>
                <span className="text-[10px] xl:text-xs text-gray-400 font-semibold uppercase mb-2 xl:mb-4 block tracking-wider truncate">Bid Analytics</span>
                
                <span className="text-[9px] xl:text-[10px] text-gray-500 font-semibold uppercase mb-1 xl:mb-2 block tracking-wider truncate">Cumulative Depth</span>
                <div className={`flex justify-between items-center text-[10px] lg:text-xs xl:text-sm p-1 xl:p-1.5 -mx-1 rounded mb-1 ${flashBidDepth3}`}>
                    <span className="text-gray-400 truncate mr-2">3 Lvs</span>
                    <span className="text-white font-mono shrink-0">{bidDepth3.toLocaleString()}</span>
                </div>
                <div className={`flex justify-between items-center text-[10px] lg:text-xs xl:text-sm p-1 xl:p-1.5 -mx-1 rounded mb-1 ${flashBidDepth5}`}>
                    <span className="text-gray-400 truncate mr-2">5 Lvs</span>
                    <span className="text-white font-mono shrink-0">{bidDepth5.toLocaleString()}</span>
                </div>
                <div className={`flex justify-between items-center text-[10px] lg:text-xs xl:text-sm p-1 xl:p-1.5 -mx-1 rounded mb-4 ${flashBidDepth10}`}>
                    <span className="text-gray-400 truncate mr-2">10 Lvs</span>
                    <span className="text-white font-mono shrink-0">{bidDepth10.toLocaleString()}</span>
                </div>

                <span className="text-[9px] xl:text-[10px] text-gray-500 font-semibold uppercase mb-1 xl:mb-2 block tracking-wider mt-2 xl:mt-4 truncate">Expected Fill (VWAP)</span>
                <div className={`flex justify-between items-center text-[10px] lg:text-xs xl:text-sm p-1 xl:p-1.5 -mx-1 rounded mb-1 ${flashVwapBid5M}`}>
                    <span className="text-gray-400 truncate mr-2">5M</span>
                    <span className="text-[#00e676] font-mono shrink-0">{vwapBid5M > 0 ? vwapBid5M.toFixed(2) : '-'}</span>
                </div>
                <div className={`flex justify-between items-center text-[10px] lg:text-xs xl:text-sm p-1 xl:p-1.5 -mx-1 rounded mb-1 ${flashVwapBid10M}`}>
                    <span className="text-gray-400 truncate mr-2">10M</span>
                    <span className="text-[#00e676] font-mono shrink-0">{vwapBid10M > 0 ? vwapBid10M.toFixed(2) : '-'}</span>
                </div>
                <div className={`flex justify-between items-center text-[10px] lg:text-xs xl:text-sm p-1 xl:p-1.5 -mx-1 rounded mb-1 ${flashVwapBid20M}`}>
                    <span className="text-gray-400 truncate mr-2">20M</span>
                    <span className="text-[#00e676] font-mono shrink-0">{vwapBid20M > 0 ? vwapBid20M.toFixed(2) : '-'}</span>
                </div>
                <div className={`flex justify-between items-center text-[10px] lg:text-xs xl:text-sm p-1 xl:p-1.5 -mx-1 rounded mb-1 ${flashVwapBid50M}`}>
                    <span className="text-gray-400 truncate mr-2">50M</span>
                    <span className="text-[#00e676] font-mono shrink-0">{vwapBid50M > 0 ? vwapBid50M.toFixed(2) : '-'}</span>
                </div>
            </div>

            {/* CENTRO: Order Book y Top of Book */}
            <div className="w-[50%] lg:w-2/4 flex flex-col gap-2 xl:gap-4 overflow-hidden min-w-[250px]">
                {/* Top of Book Compacto */}
                <div className="flex justify-between items-center bg-[#1c2237] py-2 md:py-3 px-2 md:px-8 rounded-lg border border-[#262c40] shadow-sm shrink-0 min-w-0">
                    <div className={`flex flex-col items-center px-1 md:px-4 py-1 rounded-xl min-w-0 ${flashBid}`}>
                        <span className="text-[9px] md:text-[10px] text-gray-400 font-semibold tracking-wider mb-0 md:mb-1 drop-shadow truncate">BEST BID</span>
                        <span className="text-lg md:text-2xl xl:text-3xl font-extrabold text-[#00e676] tracking-tight shrink-0">{bestBid > 0 ? bestBid.toFixed(2) : '---.--'}</span>
                    </div>
                    
                    <div className={`flex flex-col items-center justify-center px-2 lg:px-6 border-x border-[#262c40] shrink min-w-0 ${flashSpread}`}>
                        <span className="text-[9px] md:text-[10px] text-gray-500 font-semibold tracking-widest mb-0 md:mb-1 drop-shadow truncate">SPREAD</span>
                        <span className="text-[10px] md:text-base xl:text-lg font-mono text-white bg-[#101523] px-2 lg:px-4 py-0 md:py-1 rounded shadow-inner shrink-0">{spread > 0 ? spread.toFixed(2) : '-.--'}</span>
                    </div>

                    <div className={`flex flex-col items-center px-1 md:px-4 py-1 rounded-xl min-w-0 ${flashAsk}`}>
                        <span className="text-[9px] md:text-[10px] text-gray-400 font-semibold tracking-wider mb-0 md:mb-1 drop-shadow truncate">BEST ASK</span>
                        <span className="text-lg md:text-2xl xl:text-3xl font-extrabold text-[#ff3b30] tracking-tight shrink-0">{bestAsk > 0 ? bestAsk.toFixed(2) : '---.--'}</span>
                    </div>
                </div>

                {/* Grilla Order Book */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden grid grid-cols-2 bg-[#1c2237] rounded-lg border border-[#262c40] shadow-md select-none rounded-t-none border-t-0">
                    {/* BIDS */}
                    <div className="flex flex-col border-r border-[#262c40] relative h-max min-h-full">
                        <div className="sticky top-0 flex justify-between px-2 xl:px-4 py-1 xl:py-2 bg-[#161b2c] text-[9px] md:text-[10px] font-semibold text-gray-400 uppercase tracking-wider z-20 shadow-sm border-b border-[#262c40]">
                            <span>Size</span>
                            <span>Bid Price</span>
                        </div>
                        <div className="flex flex-col relative py-1">
                            {bids.length === 0 && !loading && (
                                <div className="text-center text-gray-500 py-10 text-[10px] xl:text-xs">No active bids</div>
                            )}
                            {bids.map((bid, i) => (
                                <div key={`${bid.entry_id}-${i}`} className="group relative flex justify-between items-center px-2 xl:px-4 py-1 hover:bg-[#20273c] cursor-pointer transition-colors z-10 w-full min-w-0">
                                    <div 
                                        className="absolute right-0 top-0 h-full bg-[#1e4a3b]/50 z-0 pointer-events-none" 
                                        style={{ width: `${(bid.size / maxSize) * 100}%` }}
                                    />
                                    <span className="z-10 text-gray-300 text-[10px] lg:text-xs font-medium truncate mr-1">{bid.size.toLocaleString()}</span>
                                    <span className="z-10 text-[#00e676] font-mono text-[10px] lg:text-xs tracking-wide shrink-0">{Number(bid.price).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ASKS */}
                    <div className="flex flex-col relative h-max min-h-full">
                        <div className="sticky top-0 flex justify-between px-2 xl:px-4 py-1 xl:py-2 bg-[#161b2c] text-[9px] md:text-[10px] font-semibold text-gray-400 uppercase tracking-wider z-20 shadow-sm border-b border-[#262c40]">
                            <span>Ask Price</span>
                            <span>Size</span>
                        </div>
                        <div className="flex flex-col relative py-1">
                            {asks.length === 0 && !loading && (
                                <div className="text-center text-gray-500 py-10 text-[10px] xl:text-xs">No active asks</div>
                            )}
                            {asks.map((ask, i) => (
                                <div key={`${ask.entry_id}-${i}`} className="group relative flex justify-between items-center px-2 xl:px-4 py-1 hover:bg-[#20273c] cursor-pointer transition-colors z-10 w-full min-w-0">
                                    <div 
                                        className="absolute left-0 top-0 h-full bg-[#521e25]/50 z-0 pointer-events-none" 
                                        style={{ width: `${(ask.size / maxSize) * 100}%` }}
                                    />
                                    <span className="z-10 text-[#ff3b30] font-mono text-[10px] lg:text-xs tracking-wide shrink-0 mr-1">{Number(ask.price).toFixed(2)}</span>
                                    <span className="z-10 text-gray-300 text-[10px] lg:text-xs font-medium truncate">{ask.size.toLocaleString()}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* LADO DERECHO: Analíticas de Ventas (Asks) */}
            <div className={`w-[25%] lg:w-1/4 flex flex-col p-2 xl:p-4 rounded-lg border border-[#262c40] bg-[#1c2237] overflow-y-auto shadow-md border-t-4 border-t-[#ff3b30] min-w-[120px]`}>
                <span className="text-[10px] xl:text-xs text-gray-400 font-semibold uppercase mb-2 xl:mb-4 block tracking-wider text-right truncate">Ask Analytics</span>
                
                <span className="text-[9px] xl:text-[10px] text-gray-500 font-semibold uppercase mb-1 xl:mb-2 block tracking-wider text-right truncate">Cumulative Depth</span>
                <div className={`flex justify-between items-center text-[10px] lg:text-xs xl:text-sm p-1 xl:p-1.5 -mx-1 rounded mb-1 ${flashAskDepth3}`}>
                    <span className="text-gray-400 truncate mr-2">3 Lvs</span>
                    <span className="text-white font-mono shrink-0">{askDepth3.toLocaleString()}</span>
                </div>
                <div className={`flex justify-between items-center text-[10px] lg:text-xs xl:text-sm p-1 xl:p-1.5 -mx-1 rounded mb-1 ${flashAskDepth5}`}>
                    <span className="text-gray-400 truncate mr-2">5 Lvs</span>
                    <span className="text-white font-mono shrink-0">{askDepth5.toLocaleString()}</span>
                </div>
                <div className={`flex justify-between items-center text-[10px] lg:text-xs xl:text-sm p-1 xl:p-1.5 -mx-1 rounded mb-6 ${flashAskDepth10}`}>
                    <span className="text-gray-400 truncate mr-2">10 Lvs</span>
                    <span className="text-white font-mono shrink-0">{askDepth10.toLocaleString()}</span>
                </div>

                <span className="text-[9px] xl:text-[10px] text-gray-500 font-semibold uppercase mb-1 xl:mb-2 block tracking-wider text-right mt-2 xl:mt-4 truncate">Expected Fill (VWAP)</span>
                <div className={`flex justify-between items-center text-[10px] lg:text-xs xl:text-sm p-1 xl:p-1.5 -mx-1 rounded mb-1 ${flashVwapAsk5M}`}>
                    <span className="text-gray-400 truncate mr-2">5M</span>
                    <span className="text-[#ff3b30] font-mono shrink-0">{vwapAsk5M > 0 ? vwapAsk5M.toFixed(2) : '-'}</span>
                </div>
                <div className={`flex justify-between items-center text-[10px] lg:text-xs xl:text-sm p-1 xl:p-1.5 -mx-1 rounded mb-1 ${flashVwapAsk10M}`}>
                    <span className="text-gray-400 truncate mr-2">10M</span>
                    <span className="text-[#ff3b30] font-mono shrink-0">{vwapAsk10M > 0 ? vwapAsk10M.toFixed(2) : '-'}</span>
                </div>
                <div className={`flex justify-between items-center text-[10px] lg:text-xs xl:text-sm p-1 xl:p-1.5 -mx-1 rounded mb-1 ${flashVwapAsk20M}`}>
                    <span className="text-gray-400 truncate mr-2">20M</span>
                    <span className="text-[#ff3b30] font-mono shrink-0">{vwapAsk20M > 0 ? vwapAsk20M.toFixed(2) : '-'}</span>
                </div>
                <div className={`flex justify-between items-center text-[10px] lg:text-xs xl:text-sm p-1 xl:p-1.5 -mx-1 rounded mb-1 ${flashVwapAsk50M}`}>
                    <span className="text-gray-400 truncate mr-2">50M</span>
                    <span className="text-[#ff3b30] font-mono shrink-0">{vwapAsk50M > 0 ? vwapAsk50M.toFixed(2) : '-'}</span>
                </div>
            </div>
        </div>
    </div>
  );
};

export default OrderBookView;
